import React, { useState, useContext, useEffect } from "react";
import { motion } from "framer-motion";
import {
  ShoppingCart,
  Users,
  Box,
  BarChart3,
  Settings,
  Home,
  Menu,
  X,
  TrendingUp,
  Package,
  ChevronDown,
  Receipt,
  Bell,
  Trash2,
  CheckCheck,
  LogOut,
  Shield,
  Gift,
  CreditCard,
  Heart,
  Percent,
  Building,
  Truck,
  AlertTriangle,
  UserPlus,
  MessageSquare,
  ClipboardList,
  Clipboard,
} from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { NotificationContext } from "../context/NotificationContext";
import { useAuth } from "../context/AuthContext";
import { usePermissions } from "../context/PermissionsContext";
import { useCompanySettings } from "../context/CompanySettingsContext";
import authAxios from "../api/authAxios";
import { SubscriptionCard } from "./SubscriptionCard";
import { resolveMediaUrl } from "../utils/resolveMediaUrl";
import { tokenManager } from "../utils/tokenManager";

// Helper Component for Submenu Items
function SubMenuItem({ label, icon: Icon, to, active, onClick, isCollapsed }) {
  return (
    <Link to={to} onClick={onClick}>
      <motion.div
        whileHover={{ x: 5 }}
        className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm mb-1 transition-all duration-200 ${
          isCollapsed ? "justify-center ml-0" : "ml-6"
        } ${active
          ? "text-green-600 dark:text-green-400 bg-green-50 dark:bg-white/5"
          : "text-gray-500 dark:text-white/60 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/5"
          }`}
      >
        {typeof Icon === 'string' ? (
          <img src={Icon} className="w-4 h-4 object-contain flex-shrink-0" alt={label} />
        ) : (
          <Icon className="w-4 h-4 flex-shrink-0" />
        )}
        {!isCollapsed && <span className="truncate ml-1">{label}</span>}
      </motion.div>
    </Link>
  );
}

function IconBtn({ icon: Icon, label, active, to, hasSubmenu, isOpen, onClick, subItems, isCollapsed }) {
  const content = (
    <motion.div
      whileHover={{ x: isCollapsed ? 0 : 5 }}
      className={`flex items-center group gap-3 px-4 py-3.5 rounded-2xl transition-all duration-300 ${
        isCollapsed ? "justify-center px-0" : "justify-between"
      } ${active && !hasSubmenu
        ? "bg-green-50 dark:bg-green-500/10 border-2 border-green-500/20 dark:border-green-500/40 shadow-sm"
        : "text-gray-600 dark:text-white/70 hover:text-gray-900 dark:hover:text-white/90 hover:bg-gray-100 dark:hover:bg-white/5 border-2 border-transparent"
        }`}
      title={isCollapsed ? label : ""}
    >
      <div className={`flex items-center gap-3 ${isCollapsed ? "justify-center w-full" : ""}`}>
        {typeof Icon === 'string' ? (
          <motion.div
            whileHover={{ scale: 1.1, rotate: 5 }}
            className={`flex-shrink-0 transition-all duration-200 ${active && !hasSubmenu ? 'scale-110' : 'opacity-80 group-hover:opacity-100'}`}
          >
            <img src={Icon} className="w-6 h-6 object-contain" alt={label} />
          </motion.div>
        ) : (
          <Icon className="w-5 h-5 flex-shrink-0" />
        )}
        {!isCollapsed && (
          <span className={`truncate font-semibold text-sm transition-colors duration-200 ${active && !hasSubmenu ? 'text-green-700 dark:text-green-400' : ''}`}>
            {label}
          </span>
        )}
      </div>
      {!isCollapsed && hasSubmenu && (
        <ChevronDown
          className={`w-4 h-4 transition-transform flex-shrink-0 ${isOpen ? "rotate-180" : ""
            }`}
        />
      )}
    </motion.div>
  );

  if (hasSubmenu && !isCollapsed) {
    return (
      <div className="mb-1">
        <div onClick={onClick} className="cursor-pointer">
          {content}
        </div>
        <motion.div
          initial={false}
          animate={{ height: isOpen ? "auto" : 0, opacity: isOpen ? 1 : 0 }}
          transition={{ duration: 0.2 }}
          className="overflow-hidden"
        >
          {subItems && subItems.map((sub, idx) => (
            <SubMenuItem
              key={sub.path || idx}
              label={sub.label}
              icon={sub.icon}
              to={sub.path}
              active={sub.active}
              onClick={sub.onClick}
              isCollapsed={isCollapsed}
            />
          ))}
        </motion.div>
      </div>
    );
  }

  if (to && to !== "#") {
    return (
      <Link to={to} onClick={onClick}>
        {content}
      </Link>
    );
  }

  return (
    <div onClick={onClick} className="cursor-pointer">
      {content}
    </div>
  );
}

export default function Navbar({ isCollapsed, onToggle }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, userRole } = useAuth();
  const { hasPermission } = usePermissions();
  const { companySettings } = useCompanySettings();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const { notifications, markAsRead, markAllAsRead, clearNotification, clearAllNotifications, unreadCount } = useContext(NotificationContext);
  const [backendConnected, setBackendConnected] = useState(true);
  const [openSubmenu, setOpenSubmenu] = useState(null); // Track open submenu by label

  // Check backend health periodically (only when authenticated)
  useEffect(() => {
    const checkBackend = async () => {
      const token = tokenManager.getToken();
      if (!token) {
        // Not logged in, skip health check silently
        setBackendConnected(true);
        return;
      }
      try {
        await authAxios.get('/health/');
        setBackendConnected(true);
      } catch (err) {
        // Only mark as disconnected for network errors, not auth errors
        if (!err.response || err.response.status >= 500) {
          setBackendConnected(false);
        } else {
          setBackendConnected(true);
        }
      }
    };

    checkBackend();
    const interval = setInterval(checkBackend, 30000);
    return () => clearInterval(interval);
  }, []);

  const isActive = (path) => location.pathname === path;

  // Helper to check if any child is active
  const isParentActive = (item) => {
    if (item.path && isActive(item.path)) return true;
    if (item.subItems) {
      return item.subItems.some(sub => isActive(sub.path));
    }
    return false;
  };

  const toggleSubmenu = (label) => {
    setOpenSubmenu(openSubmenu === label ? null : label);
  };

  // Auto-open submenu if path matches
  useEffect(() => {
    // Find nested item
    const findParent = (items) => {
      for (const item of items) {
        if (item.subItems) {
          if (item.subItems.some(sub => isActive(sub.path))) {
            return item.label;
          }
        }
      }
      return null;
    };
    // We'll define coreItems inside rendering or use a state-independent definition if possible.
    // However, coreItems depends on roles/permissions which are relatively static.
    // For now, let's keep it simple.
  }, [location.pathname]);


  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "sale": return "🛍️";
      case "product": return "📦";
      case "transit": return "🚚";
      default: return "📢";
    }
  };

  const formatTime = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  // Core Features
  // Refactored to include submenus
  const coreItemsDefinition = [
    // Owner Dashboard
    { label: "Dashboard", icon: "https://cdn-icons-png.flaticon.com/512/2329/2329087.png", path: "/owner/dashboard", roles: ["OWNER"], permission: "view_owner_dashboard" },
    
    // Sales Executive Dashboard
    { label: "Dashboard", icon: "https://cdn-icons-png.flaticon.com/512/2329/2329087.png", path: "/sales-executive/dashboard", roles: ["SALES_EXECUTIVE"], permission: "view_employee_dashboard" },

    // Staff / General Dashboard
    { label: "Dashboard", icon: "https://cdn-icons-png.flaticon.com/512/2329/2329087.png", path: "/dashboard", roles: ["STAFF", "MANAGER", "ACCOUNTANT", "INVENTORY_STAFF", "BILLING_STAFF"], permission: "view_employee_dashboard" },
    
    // POS Billing
    { label: "POS Billing", icon: "https://cdn-icons-png.flaticon.com/512/17940/17940025.png", path: "/pos", roles: ["OWNER", "SALES_EXECUTIVE"], permission: "view_billing" },
    
    // Attendance for Sales Executive
    { label: "Mark Attendance", icon: Clipboard, path: "/sales-executive/attendance", roles: ["SALES_EXECUTIVE"] },
    
    // Invoice History - Show to both Owner and Sales Executive
    { label: "Invoice History", icon: "https://cdn-icons-png.flaticon.com/512/12061/12061077.png", path: "/invoices", roles: ["OWNER", "SALES_EXECUTIVE"], permission: "export_billing" },
    
    // Collections for Sales Executive
    { label: "Track Collections", icon: CreditCard, path: "/sales-executive/collections", roles: ["SALES_EXECUTIVE"] },
    
    // Leave Requests for Sales Executive
    { label: "Apply Leave", icon: AlertTriangle, path: "/sales-executive/leaves", roles: ["SALES_EXECUTIVE"] },
    
    // Salary & Incentives for Sales Executive
    { label: "Salary & Incentives", icon: Gift, path: "/sales-executive/incentives-payroll", roles: ["SALES_EXECUTIVE"] },
    
    // Reports (Owner only)
    { label: "Reports", icon: "https://cdn-icons-png.flaticon.com/512/2912/2912773.png", path: "/reports", roles: ["OWNER"], permission: "view_reports" },
    { label: "Purchase Requests", icon: Clipboard, path: "/owner/purchase-requests", roles: ["OWNER"], permission: "view_purchase" },

    // Super Admin Group
    { label: "Super Admin", icon: Shield, path: "/super-admin", roles: ["SUPERADMIN"] },
    { label: "Razorpay Config", icon: CreditCard, path: "/super-admin?tab=razorpay_config", roles: ["SUPERADMIN"] },
    { label: "Leads", icon: Users, path: "/super-admin/leads", roles: ["SUPERADMIN"] },
    { label: "Contact Us Leads", icon: MessageSquare, path: "/super-admin/contact-leads", roles: ["SUPERADMIN"] },
    { label: "Account Requests", icon: ClipboardList, path: "/account-requests", roles: ["SUPERADMIN"] },

    // Inventory Group
    {
      label: "Inventory",
      icon: "https://cdn-icons-png.flaticon.com/512/16128/16128301.png",
      roles: ["OWNER"],
      permission: "view_inventory",
      hasSubmenu: true,
      subItems: [
        { label: "All Products", icon: Box, path: "/inventory", roles: ["OWNER"], permission: "view_inventory" },
        { label: "Smart Purchase Bills", icon: Receipt, path: "/smart-purchase", roles: ["OWNER"], permission: "create_purchase" },
        { label: "Stock Inward", icon: Truck, path: "/stock-inward", roles: ["OWNER"], permission: "create_inventory" },
        { label: "Suppliers", icon: Users, path: "/suppliers", roles: ["OWNER"], permission: "view_vendors" },
        { label: "Categories", icon: Package, path: "/owner/categories", roles: ["OWNER"], permission: "edit_inventory" },
      ]
    },

    { label: "Customers", icon: "https://cdn-icons-png.flaticon.com/512/1067/1067566.png", path: "/customers", roles: ["OWNER", "SALES_EXECUTIVE"], permission: "view_customers" },

    // Expanded Employees Sub-menu for Owner
    {
      label: "Employees",
      icon: "https://cdn-icons-png.flaticon.com/512/9759/9759919.png",
      roles: ["OWNER"],
      permission: "view_employees",
      hasSubmenu: true,
      subItems: [
        { label: "All Employees", icon: Users, path: "/employees", roles: ["OWNER"], permission: "view_employees" },
        { label: "Add Employee", icon: UserPlus, path: "/employees/add", roles: ["OWNER"], permission: "create_employees" },
        { label: "Attendance Reports", icon: Clipboard, path: "/employees/attendance", roles: ["OWNER"], permission: "view_attendance" },
        { label: "Leave Approvals", icon: AlertTriangle, path: "/employees/leaves", roles: ["OWNER"], permission: "approve_attendance" },
        { label: "Payroll Management", icon: CreditCard, path: "/employees/payroll", roles: ["OWNER"], permission: "view_payroll" },
        { label: "Employee Performance", icon: TrendingUp, path: "/employees/tracking", roles: ["OWNER"], permission: "view_sales" },
      ]
    },
  ];

  // Settings Items
  const settingsItems = [
    { label: "Profile Settings", icon: "https://cdn-icons-png.flaticon.com/512/3524/3524752.png", path: "/settings", roles: ["OWNER"], permission: "view_settings" },
    { label: "Notifications", icon: "https://cdn-icons-png.flaticon.com/512/2950/2950678.png", path: "/notifications", roles: ["OWNER", "SALES_EXECUTIVE"], permission: "view_notifications" },
    { label: "Roles & Permissions", icon: Shield, path: "/owner/roles-permissions", roles: ["SUPERADMIN"] },
    { label: "Subscription", icon: "https://cdn-icons-png.flaticon.com/512/4659/4659011.png", path: "/owner/subscription-management", roles: ["OWNER"], permission: "view_payments" },
    { label: "Support", icon: "https://cdn-icons-png.flaticon.com/512/2951/2951105.png", path: "/support", roles: ["OWNER", "SALES_EXECUTIVE"], permission: "view_services" },
  ];

  // Helper to determine if a sidebar item is visible
  const checkVisibility = (item) => {
    // Force strict role check for Dashboard to keep the UI separated
    if (item.label === "Dashboard") {
      return item.roles && item.roles.includes(userRole);
    }
    // For other items, check database permission if it exists
    if (item.permission) {
      return hasPermission(userRole, item.permission);
    }
    // Fallback to strict role match if no database permission exists
    if (item.roles) {
      return item.roles.includes(userRole);
    }
    return true;
  };

  const visibleCoreItems = coreItemsDefinition.reduce((acc, item) => {
    // Handle subitems if they exist
    if (item.subItems) {
      const filteredSubItems = item.subItems.filter(sub => checkVisibility(sub));
      // Show parent if parent check passes OR if it has at least one visible child item
      if (checkVisibility(item) || filteredSubItems.length > 0) {
        acc.push({ ...item, subItems: filteredSubItems });
      }
    } else {
      if (checkVisibility(item)) {
        acc.push(item);
      }
    }

    return acc;
  }, []);

  const visibleSettingsItems = settingsItems.filter(item => checkVisibility(item));

  // Auto-expand Inventory if on a sub-page
  useEffect(() => {
    visibleCoreItems.forEach(item => {
      if (item.hasSubmenu && item.subItems) {
        if (item.subItems.some(sub => isActive(sub.path))) {
          setOpenSubmenu(item.label);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);


  return (
    <>
      {/* Desktop Sidebar */}
      <aside className={`hidden md:flex fixed left-0 top-0 h-screen transition-all duration-300 ${
        isCollapsed ? "w-20" : "w-72"
      } bg-white dark:bg-gradient-to-b dark:from-dark-sidebar dark:to-dark-sidebar/95 text-gray-900 dark:text-white flex-col border-r border-gray-200 dark:border-white/5 z-50`}>
        {/* Logo Section - Fixed Top */}
        <div className={`border-b border-gray-100 dark:border-white/5 flex flex-col items-center transition-all duration-300 ${isCollapsed ? "p-4 gap-4" : "p-6 gap-2"}`}>
          <div className={`flex items-center w-full ${isCollapsed ? "flex-col justify-center gap-4" : "justify-between"}`}>
            <div className={`flex items-center transition-all duration-300 ${isCollapsed ? "flex-col gap-2" : "gap-3"}`}>
              <div className={`flex-shrink-0 rounded-2xl flex items-center justify-center font-bold shadow-xl overflow-hidden bg-white p-1.5 transition-all duration-300 border border-gray-100 dark:border-transparent ${
                isCollapsed ? "w-10 h-10" : "w-12 h-12"
              }`}>
                <img src="/brand-logo.png" alt="Gio Billing Logo" className="w-full h-full object-cover scale-150" />
              </div>
              {!isCollapsed && (
                <div className="min-w-0">
                  <h1 className="font-bold text-lg tracking-tight text-gray-900 dark:text-white">Gio Billing</h1>
                  <p className="text-[10px] uppercase tracking-widest text-gray-500 dark:text-white/40 font-bold">Premium Suite</p>
                </div>
              )}
            </div>
            
            <button 
              onClick={onToggle}
              className={`p-2 rounded-xl transition-all duration-200 border border-gray-200 dark:border-white/5 ${
                isCollapsed 
                  ? "bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/15 text-gray-600 dark:text-white/80" 
                  : "bg-transparent hover:bg-gray-100 dark:hover:bg-white/10 text-gray-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              {isCollapsed ? <Menu className="w-5 h-5" /> : <X className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Notifications Panel - Fixed Top */}
        <div className="flex-shrink-0 relative border-b border-gray-100 dark:border-white/10 p-4">
          <button
            onClick={() => setNotificationOpen(!notificationOpen)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gray-100 dark:bg-white/10 hover:bg-gray-200 dark:hover:bg-white/15 transition-colors relative"
          >
            <img src="https://cdn-icons-png.flaticon.com/512/2950/2950678.png" className="w-6 h-6 object-contain" alt="Notifications" />
            {!isCollapsed && unreadCount > 0 && (
              <span className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
            {isCollapsed && unreadCount > 0 && (
              <span className="absolute top-2 right-2 w-2 h-2 bg-red-500 rounded-full" />
            )}
          </button>

          {/* Notification Dropdown */}
          {notificationOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="absolute left-4 right-4 top-full mt-2 bg-white dark:bg-dark-sidebar border border-gray-100 dark:border-white/10 rounded-2xl shadow-2xl max-h-96 overflow-y-auto z-50 ring-1 ring-black/5 dark:ring-white/5"
            >
              {notifications.length === 0 ? (
                <div className="p-8 text-center text-gray-500 dark:text-white/60">
                  <img src="https://cdn-icons-png.flaticon.com/512/2950/2950678.png" className="w-12 h-12 mx-auto mb-3 opacity-40 grayscale" alt="No notifications" />
                  <p className="text-sm font-medium italic">No notifications yet</p>
                  <p className="text-[10px] mt-1 opacity-60">When you receive notifications, they will appear here.</p>
                </div>
              ) : (
                <>
                  <div className="divide-y divide-white/5">
                    {notifications.map((notif) => (
                      <motion.div
                        key={notif.id}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className={`p-4 cursor-pointer transition-all duration-200 ${!notif.read
                          ? "bg-blue-50/50 dark:bg-white/10 hover:bg-blue-50 dark:hover:bg-white/15"
                          : "bg-transparent hover:bg-gray-50 dark:hover:bg-white/3 opacity-60"
                          }`}
                        onClick={() => {
                          markAsRead(notif.id);
                          setNotificationOpen(false);
                          navigate("/notifications");
                        }}
                      >
                        <div className="flex gap-3">
                          <span className={`text-xl flex-shrink-0 transition-opacity ${!notif.read ? "" : "opacity-50"
                            }`}>
                            {getNotificationIcon(notif.type)}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold truncate transition-colors ${!notif.read ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-white/60"
                              }`}>
                              {notif.title}
                            </p>
                            <p className={`text-xs line-clamp-2 mt-1 transition-colors ${!notif.read ? "text-gray-600 dark:text-white/60" : "text-gray-400 dark:text-white/40"
                              }`}>
                              {typeof notif.message === 'string' ? notif.message : 'Notification'}
                            </p>
                            <p className={`text-[10px] mt-2 transition-colors font-medium ${!notif.read ? "text-blue-500 dark:text-white/40" : "text-gray-400 dark:text-white/30"
                              }`}>
                              {formatTime(notif.timestamp)}
                            </p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              clearNotification(notif.id);
                            }}
                            className={`transition-colors p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 ${!notif.read
                              ? "text-gray-400 dark:text-white/40 hover:text-gray-600 dark:hover:text-white/70"
                              : "text-gray-300 dark:text-white/20 hover:text-gray-500 dark:hover:text-white/40"
                              }`}
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="border-t border-gray-100 dark:border-white/5 p-3 space-y-2 bg-gray-50/50 dark:bg-transparent">
                    <div className="flex gap-2">
                      <button
                        onClick={() => markAllAsRead()}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-white/70 hover:text-blue-600 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 rounded-xl transition-all border border-transparent hover:border-gray-100 shadow-sm hover:shadow"
                      >
                        <CheckCheck className="w-3.5 h-3.5" />
                        Mark all read
                      </button>
                      <button
                        onClick={() => clearAllNotifications()}
                        className="flex-1 flex items-center justify-center gap-2 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-gray-500 dark:text-white/70 hover:text-red-600 dark:hover:text-white hover:bg-white dark:hover:bg-white/5 rounded-xl transition-all border border-transparent hover:border-gray-100 shadow-sm hover:shadow"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Clear all
                      </button>
                    </div>
                    <Link
                      to="/notifications"
                      onClick={() => setNotificationOpen(false)}
                      className="flex items-center justify-center w-full px-3 py-2 text-xs font-bold text-blue-600 dark:text-green-500 hover:text-blue-700 dark:hover:text-green-400 bg-blue-50 dark:bg-green-500/10 rounded-xl transition-all"
                    >
                      View All Notifications
                    </Link>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </div>

        {/* Scrollable Navigation Area - Takes remaining height */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2 min-h-0">
          {/* Core Items */}
          {visibleCoreItems.map((item) => (
            <IconBtn
              key={item.label} 
              {...item}
              isCollapsed={isCollapsed}
              active={isParentActive(item)}
              to={item.path}
              isOpen={openSubmenu === item.label}
              onClick={() => {
                if (item.hasSubmenu) {
                  toggleSubmenu(item.label);
                }
              }}
              // Map subItems to include active state
              subItems={item.subItems ? item.subItems.map(sub => ({
                ...sub,
                active: isActive(sub.path)
              })) : undefined}
            />
          ))}

          {/* Settings Section */}
          {visibleSettingsItems.length > 0 && (
            <div className="pt-2 border-t border-gray-100 dark:border-white/5 mt-2">
              {visibleSettingsItems.map((item) => (
                <IconBtn key={item.path} {...item} isCollapsed={isCollapsed} active={isActive(item.path)} to={item.path} />
              ))}
              

            </div>
          )}
        </nav>

        {/* Bottom Section - Fixed Bottom */}
        <div className="flex-shrink-0 p-4 border-t border-gray-100 dark:border-white/10 space-y-2 bg-white dark:bg-dark-sidebar z-10">
          {/* {userRole === "OWNER" && <SubscriptionCard />} */}

            <div className={`p-3 rounded-xl bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors cursor-pointer flex items-center gap-3 ${
              isCollapsed ? "justify-center px-0" : ""
            }`}>
              <div className={`rounded-full bg-gradient-primary flex items-center justify-center font-bold overflow-hidden flex-shrink-0 transition-all duration-300 shadow-sm ${
                isCollapsed ? "w-8 h-8" : "w-10 h-10"
              }`}>
                {companySettings?.logo ? (
                  <img
                    src={resolveMediaUrl(companySettings.logo)}
                    alt="Company Logo"
                    className="w-full h-full object-cover scale-150"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = "/default-logo.png";
                    }}
                  />
                ) : (
                  <span>{userRole === "OWNER" ? "👨‍💼" : "💼"}</span>
                )}
              </div>
              {!isCollapsed && (
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate text-gray-900 dark:text-white">{user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : (user?.name || "User")}</p>
                  <p className="text-xs text-gray-500 dark:text-white/60 truncate">+91 {user?.phone || "N/A"}</p>
                </div>
              )}
            </div>


          {/* Hide logout button for OWNER role */}

          {/* Hide logout button for OWNER role */}
          {/* Backend Status Indicator */}
          <div className={`flex items-center justify-center gap-2 mb-3 px-4 py-2 bg-gray-100 dark:bg-white/5 rounded-lg ${
            isCollapsed ? "mx-2 px-1" : "mx-4"
          }`}>
            <div className={`w-2 h-2 rounded-full ${backendConnected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
            {!isCollapsed && (
              <span className={`text-xs font-medium ${backendConnected ? 'text-green-600' : 'text-red-400'}`}>
                {backendConnected ? "System Online" : "System Offline"}
              </span>
            )}
          </div>

          <button
            onClick={handleLogout}
            className={`w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-300 hover:text-red-200 rounded-xl font-medium transition-all ${
              isCollapsed ? "px-0" : ""
            }`}
          >
            <LogOut className="w-4 h-4 text-red-400" />
            {!isCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside >

      {/* Mobile Header (Classic) */}
      < div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-gradient-to-r from-dark-sidebar to-dark-sidebar/90 text-white flex items-center justify-between px-4 z-50 border-b border-white/5" >
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-gradient-primary flex items-center justify-center font-bold overflow-hidden border border-white/10 shadow-lg">
            {companySettings.logo ? (
              <img
                src={resolveMediaUrl(companySettings.logo)}
                alt="Company Logo"
                className="w-full h-full object-cover scale-150"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "/default-logo.png";
                }}
              />
            ) : (
              <span>💰</span>
            )}
          </div>
          <span className="font-bold">Gio Billing</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setNotificationOpen(!notificationOpen)}
            className="relative p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <Bell size={20} />
            {unreadCount > 0 && (
              <span className="absolute top-0 right-0 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            {mobileOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div >

      {/* Mobile Menu */}
      {
        mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden fixed top-16 left-0 right-0 bg-dark-sidebar text-white border-b border-white/5 p-4 space-y-2 z-40 max-h-96 overflow-y-auto"
          >
            {visibleCoreItems.map((item) => (
              <IconBtn
                key={item.label}
                {...item}
                active={isParentActive(item)}
                to={item.path}
                isOpen={openSubmenu === item.label}
                onClick={() => {
                  if (item.hasSubmenu) {
                    toggleSubmenu(item.label);
                  } else {
                    setMobileOpen(false);
                  }
                }}
                subItems={item.subItems ? item.subItems.map(sub => ({
                  ...sub,
                  active: isActive(sub.path),
                  onClick: () => setMobileOpen(false)
                })) : undefined}
              />
            ))}

            {/* Mobile Settings */}
            {visibleSettingsItems.length > 0 && (
              <div className="pt-2 border-t border-white/5 mt-2">
                {visibleSettingsItems.map((item) => (
                  <IconBtn key={item.path} {...item} active={isActive(item.path)} to={item.path} onClick={() => setMobileOpen(false)} />
                ))}
                

              </div>
            )}
          </motion.div>
        )
      }
    </>
  );
}
